import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Upload, Image as ImageIcon, AlertCircle, CheckCircle2, Loader2, Rocket, Users, Building, Clock, Sparkles, ArrowRight, Trash2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml'];
const MAX_SIZE = 2 * 1024 * 1024; // 2MB

const fadeIn = (delay = 0) => ({
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1], delay },
});

/* ── Radio Pill Group ── */
const RadioPills = ({ name, options, value, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((opt) => (
      <button
        key={opt.value}
        type="button"
        onClick={() => onChange(opt.value)}
        className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
          value === opt.value
            ? 'bg-brand-blue text-white shadow-md shadow-brand-blue/20'
            : 'bg-white border border-[color:var(--hairline)] text-slate-600 hover:border-brand-blue/30 hover:bg-brand-blue/[0.03]'
        }`}
        data-testid={`${name}-${opt.value}`}
      >
        <opt.icon className="w-4 h-4" />
        {opt.label}
      </button>
    ))}
  </div>
);

/* ── Auto-resize Textarea ── */
const AutoTextarea = ({ value, onChange, placeholder, testId, minRows = 3 }) => {
  const ref = useRef(null);
  const handleChange = (e) => {
    onChange(e.target.value);
    if (ref.current) {
      ref.current.style.height = 'auto';
      ref.current.style.height = ref.current.scrollHeight + 'px';
    }
  };
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      rows={minRows}
      className="w-full px-4 py-3.5 rounded-xl bg-white border border-[color:var(--hairline)] text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-shadow duration-200 resize-none leading-relaxed"
      data-testid={testId}
    />
  );
};

/* ── Logo Upload Zone ── */
const LogoUpload = ({ file, preview, onSelect, onRemove, error }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = (files) => {
    if (files.length > 0) onSelect(files[0]);
  };

  return (
    <div>
      {preview ? (
        <div className="flex items-center gap-4 p-3 rounded-xl border border-[color:var(--hairline)] bg-slate-50">
          <img src={preview} alt="Logo preview" className="w-14 h-14 rounded-lg object-contain border border-slate-200 bg-white p-1" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate">{file?.name}</p>
            <p className="text-[11px] text-slate-400">{file ? (file.size / 1024).toFixed(0) + 'KB' : ''}</p>
          </div>
          <button type="button" onClick={onRemove} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-red-50 transition-colors" data-testid="logo-remove">
            <Trash2 className="w-4 h-4 text-red-400" />
          </button>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(Array.from(e.dataTransfer.files)); }}
          onClick={() => inputRef.current?.click()}
          className={`flex flex-col items-center justify-center py-8 rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200 ${
            dragOver ? 'border-brand-blue bg-brand-blue/[0.03]' : 'border-slate-200 hover:border-brand-blue/30 bg-slate-50/50'
          }`}
          data-testid="logo-dropzone"
        >
          <input ref={inputRef} type="file" accept=".png,.jpg,.jpeg,.svg" className="hidden" onChange={(e) => handleFiles(Array.from(e.target.files || []))} data-testid="logo-input" />
          <ImageIcon className="w-8 h-8 text-slate-300 mb-2" />
          <p className="text-xs text-slate-500">Drag & drop your logo, or <span className="text-brand-blue font-medium">browse</span></p>
          <p className="text-[10px] text-slate-400 mt-1">PNG, JPG, or SVG &middot; Max 2MB</p>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 mt-2 text-xs text-red-500">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />{error}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════
   VISION FUNNEL MODAL
   ═══════════════════════════════════ */
export const VisionFunnel = ({ isOpen, onClose }) => {
  const [form, setForm] = useState({
    companyName: '', northStar: '', marketScale: '', bottleneck: '', timeHorizon: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoError, setLogoError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleLogoSelect = useCallback((file) => {
    setLogoError('');
    if (!ALLOWED_TYPES.includes(file.type)) {
      setLogoError('Only PNG, JPG, and SVG files are accepted.');
      return;
    }
    if (file.size > MAX_SIZE) {
      setLogoError(`File is ${(file.size / 1024 / 1024).toFixed(1)}MB. Maximum is 2MB.`);
      return;
    }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setLogoPreview(e.target.result);
    reader.readAsDataURL(file);
  }, []);

  const handleLogoRemove = () => { setLogoFile(null); setLogoPreview(null); setLogoError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = new FormData();
      payload.append('company_name', form.companyName);
      payload.append('north_star', form.northStar);
      payload.append('market_scale', form.marketScale);
      payload.append('bottleneck', form.bottleneck);
      payload.append('time_horizon', form.timeHorizon);
      if (logoFile) payload.append('logo', logoFile);
      await axios.post(`${API}/vision/submit`, payload, { headers: { 'Content-Type': 'multipart/form-data' } }).catch(() => { /* endpoint may not exist yet */ });
      setSubmitted(true);
    } catch (err) { /* non-blocking */ }
    finally { setSubmitting(false); }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setForm({ companyName: '', northStar: '', marketScale: '', bottleneck: '', timeHorizon: '' });
      setLogoFile(null); setLogoPreview(null); setLogoError(''); setSubmitted(false);
    }, 300);
  };

  const isValid = form.companyName.trim() && form.northStar.trim() && form.marketScale && form.bottleneck.trim() && form.timeHorizon;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="vision-funnel">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />
          <motion.div
            className="relative bg-white w-full max-w-2xl rounded-2xl shadow-2xl border border-[color:var(--hairline)] flex flex-col"
            style={{ maxHeight: 'min(92vh, 800px)' }}
            initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Header */}
            <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-[color:var(--hairline)]">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-brand-blue/10 flex items-center justify-center"><Sparkles className="w-5 h-5 text-brand-blue" /></div>
                <div>
                  <h2 className="text-sm font-bold text-slate-900">{submitted ? 'Vision Captured' : 'Executive Vision Brief'}</h2>
                  <p className="text-[11px] text-slate-400">Define your product vision</p>
                </div>
              </div>
              <button onClick={handleClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-slate-100 transition-colors" data-testid="vision-close"><X className="w-4 h-4 text-slate-400" /></button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <AnimatePresence mode="wait">
                {submitted ? (
                  <motion.div key="success" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center justify-center py-12 text-center" data-testid="vision-success">
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200, delay: 0.1 }} className="w-20 h-20 rounded-full bg-green-50 border-4 border-green-100 flex items-center justify-center mb-6">
                      <CheckCircle2 className="w-10 h-10 text-green-500" />
                    </motion.div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">Vision Captured.</h3>
                    <p className="text-sm text-slate-500 leading-relaxed max-w-sm">Our lead engineers are analyzing your requirements and building your custom technical architecture. You will receive a detailed blueprint within 24 hours.</p>
                  </motion.div>
                ) : (
                  <motion.form key="form" onSubmit={handleSubmit} className="space-y-7">

                    {/* Section: Company Identity */}
                    <motion.div {...fadeIn(0)}>
                      <p className="text-[10px] font-bold text-brand-blue uppercase tracking-widest mb-4">Company Identity</p>
                      <div className="space-y-4">
                        <div>
                          <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Company Name</label>
                          <input type="text" value={form.companyName} onChange={e => update('companyName', e.target.value)} required placeholder="e.g., Acme Corp" className="w-full px-4 py-3 rounded-xl bg-white border border-[color:var(--hairline)] text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-shadow" data-testid="vision-company" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Brand Logo <span className="text-slate-400 font-normal">(Optional)</span></label>
                          <LogoUpload file={logoFile} preview={logoPreview} onSelect={handleLogoSelect} onRemove={handleLogoRemove} error={logoError} />
                        </div>
                      </div>
                    </motion.div>

                    <div className="h-px bg-slate-100" />

                    {/* Section: The Vision */}
                    <motion.div {...fadeIn(0.1)}>
                      <p className="text-[10px] font-bold text-brand-orange uppercase tracking-widest mb-4">The Vision</p>
                      <div className="space-y-5">
                        <div>
                          <label className="text-xs font-semibold text-slate-700 mb-1.5 block">The North Star</label>
                          <p className="text-[11px] text-slate-400 mb-2">What is the ultimate business objective of this digital asset?</p>
                          <AutoTextarea value={form.northStar} onChange={v => update('northStar', v)} placeholder="e.g., Build a marketplace that connects local artisans with global buyers, generating $1M ARR within 18 months..." testId="vision-northstar" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-700 mb-2 block">The Market Scale</label>
                          <p className="text-[11px] text-slate-400 mb-3">Who is the primary end-user?</p>
                          <RadioPills name="market" value={form.marketScale} onChange={v => update('marketScale', v)} options={[
                            { value: 'internal', label: 'Internal Operations', icon: Building },
                            { value: 'b2b', label: 'B2B Enterprise', icon: Users },
                            { value: 'consumer', label: 'Mass Market Consumers', icon: Rocket },
                          ]} />
                        </div>
                      </div>
                    </motion.div>

                    <div className="h-px bg-slate-100" />

                    {/* Section: The Execution */}
                    <motion.div {...fadeIn(0.2)}>
                      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-4">The Execution</p>
                      <div className="space-y-5">
                        <div>
                          <label className="text-xs font-semibold text-slate-700 mb-1.5 block">The Current Bottleneck</label>
                          <p className="text-[11px] text-slate-400 mb-2">What is the primary friction point preventing this from existing today?</p>
                          <AutoTextarea value={form.bottleneck} onChange={v => update('bottleneck', v)} placeholder="e.g., We have no internal engineering team and traditional agencies quoted 6 months and $80k..." testId="vision-bottleneck" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-700 mb-2 block">The Time Horizon</label>
                          <p className="text-[11px] text-slate-400 mb-3">When must this be live?</p>
                          <RadioPills name="timeline" value={form.timeHorizon} onChange={v => update('timeHorizon', v)} options={[
                            { value: 'asap', label: '15-Day Sprint (ASAP)', icon: Rocket },
                            { value: '1-3months', label: '1-3 Months', icon: Clock },
                            { value: 'next-quarter', label: 'Next Quarter', icon: Clock },
                          ]} />
                        </div>
                      </div>
                    </motion.div>

                    {/* Submit */}
                    <motion.div {...fadeIn(0.3)} className="pt-2">
                      <button type="submit" disabled={!isValid || submitting}
                        className="w-full py-4 rounded-xl bg-gradient-to-r from-brand-blue to-brand-blue-dark text-white text-sm font-bold shadow-lg shadow-brand-blue/20 hover:shadow-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                        data-testid="vision-submit">
                        {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Submitting Vision...</>) : (<>Submit Vision for Technical Analysis <ArrowRight className="w-4 h-4" /></>)}
                      </button>
                    </motion.div>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
